import { useState, useRef } from 'react';
import { storage } from '../lib/api';
import { useSettings, useUpdateSettings, useChangePassword } from '../hooks/useSettings';
import { useAuth } from '../contexts/AuthContext';
import { Camera, Save, Lock, User, Settings } from 'lucide-react';
import FormInput from '../components/ui/FormInput';
import PageHeader from '../components/PageHeader';
import { useToast, ToastContainer } from '../components/ui/Toast';

export default function AdminSettingsPage() {
  const { user: authUser } = useAuth();
  const { toasts, addToast, removeToast } = useToast();

  const { data: settingsData, isLoading: loading } = useSettings();
  const profile = settingsData?.user ?? null;

  const updateMutation = useUpdateSettings();
  const passwordMutation = useChangePassword();

  // Profile form
  const [name, setName] = useState('');

  // Photo
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwErrors, setPwErrors] = useState<Record<string, string>>({});

  // Sync name from query data
  const [nameInitialized, setNameInitialized] = useState(false);
  if (profile && !nameInitialized) {
    setName(profile.name);
    setNameInitialized(true);
  }

  const handleSaveName = async () => {
    if (!name.trim()) return;
    updateMutation.mutate({ name: name.trim() }, {
      onSuccess: () => addToast('success', 'Nombre actualizado'),
      onError: () => addToast('error', 'Error al guardar el nombre'),
    });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      addToast('error', 'Solo se permiten imágenes');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      addToast('error', 'La imagen no puede superar 2 MB');
      return;
    }

    setUploading(true);
    try {
      // Upload to R2 via generic upload endpoint
      const key = `users/${authUser?.id}/photo.${file.name.split('.').pop()}`;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('key', key);

      const res = await fetch('/api/storage/upload', {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
      });

      if (!res.ok) throw new Error('Upload failed');

      // Update profile with photo key
      updateMutation.mutate({ photo_key: key }, {
        onSuccess: () => addToast('success', 'Foto actualizada'),
        onError: () => addToast('error', 'Error al subir la foto'),
      });
    } catch {
      addToast('error', 'Error al subir la foto');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleChangePassword = async () => {
    const errors: Record<string, string> = {};
    if (!currentPassword) errors.currentPassword = 'Requerido';
    if (!newPassword) errors.newPassword = 'Requerido';
    else if (newPassword.length < 8) errors.newPassword = 'Mínimo 8 caracteres';
    if (newPassword !== confirmPassword) errors.confirmPassword = 'Las contraseñas no coinciden';

    setPwErrors(errors);
    if (Object.keys(errors).length > 0) return;

    passwordMutation.mutate({ currentPassword, newPassword }, {
      onSuccess: () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPwErrors({});
        addToast('success', 'Contraseña actualizada');
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : 'Error al cambiar la contraseña';
        addToast('error', msg);
      },
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-64 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (!profile) {
    return <div className="text-center py-12 text-gray-500">Error cargando perfil</div>;
  }

  const photoUrl = profile.photo_key ? storage.inlineUrl(profile.photo_key) : null;

  return (
    <div className="max-w-2xl">
      <PageHeader type="settings" icon={Settings} title="Ajustes" />

      {/* Profile section */}
      <section className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
          <User size={20} className="text-teal-600" />
          Perfil
        </h2>

        {/* Avatar */}
        <div className="flex items-center gap-6 mb-6">
          <div className="relative group">
            <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center text-2xl font-bold text-teal-700 overflow-hidden">
              {photoUrl ? (
                <img src={photoUrl} alt={profile.name} className="w-full h-full object-cover" />
              ) : (
                profile.name.charAt(0).toUpperCase()
              )}
            </div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
            >
              <Camera size={20} className="text-white" />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </div>
          <div>
            <p className="font-medium text-gray-900">{profile.name}</p>
            <p className="text-sm text-gray-500">{profile.email}</p>
            {uploading && <p className="text-xs text-teal-600 mt-1">Subiendo foto...</p>}
          </div>
        </div>

        {/* Name */}
        <div className="flex items-end gap-3">
          <FormInput
            label="Nombre"
            value={name}
            onChange={e => setName(e.target.value)}
            className="flex-1"
          />
          <button
            onClick={handleSaveName}
            disabled={updateMutation.isPending || name.trim() === profile.name}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 h-[38px]"
          >
            <Save size={16} />
            {updateMutation.isPending ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </section>

      {/* Password section */}
      <section className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
          <Lock size={20} className="text-teal-600" />
          Cambiar contraseña
        </h2>

        <div className="space-y-4">
          <FormInput
            label="Contraseña actual"
            type="password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            error={pwErrors.currentPassword}
            autoComplete="current-password"
          />
          <FormInput
            label="Nueva contraseña"
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            error={pwErrors.newPassword}
            hint="Mínimo 8 caracteres"
            autoComplete="new-password"
          />
          <FormInput
            label="Confirmar contraseña"
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            error={pwErrors.confirmPassword}
            autoComplete="new-password"
          />

          <button
            onClick={handleChangePassword}
            disabled={passwordMutation.isPending}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Lock size={16} />
            {passwordMutation.isPending ? 'Cambiando...' : 'Cambiar contraseña'}
          </button>
        </div>
      </section>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
